import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../context";
import { createCTScanRecord, fetchCTScanTests } from "../../../../../api/ct_scan";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000/api").replace(/\/$/, "");
const formInputSx = {
  "& .MuiInputBase-input": { py: 1.45, px: 1.75 },
  "& .MuiInputBase-inputMultiline": { py: 1.2, px: 1.75 },
};

const CTScanReferral = () => {
  const { t } = useTranslation();
  const token = useAuth()?.user?.token;
  const headers = token ? { Authorization: `Token ${token}` } : {};

  const [tests, setTests] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketSearch, setTicketSearch] = useState("");
  const [ticketOpen, setTicketOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [form, setForm] = useState({
    ticket: "",
    patient: "",
    patient_name: "",
    test: "",
    amount: "",
    discount: "",
    is_urgent: false,
    date: new Date().toISOString().slice(0, 10),
    notes: "",
  });

  const load = async () => {
    const [tst, tkt] = await Promise.all([
      fetchCTScanTests(token),
      axios.get(`${API}/tickets/`, { headers }).then((r) => r.data),
    ]);
    setTests(Array.isArray(tst) ? tst : []);
    setTickets(tkt?.results || tkt || []);
  };

  useEffect(() => {
    if (token) load();
  }, [token]);

  const handleTestChange = (testId) => {
    const selected = tests.find((t) => String(t.id) === String(testId));
    const testAmount = selected?.amount ?? selected?.price ?? "";
    setForm((prev) => ({ ...prev, test: testId, amount: testAmount }));
  };

  const handleSubmit = async () => {
    if (!form.ticket || !form.patient || !form.test || !form.date) return;
    const amount = form.amount === "" ? 0 : Number(form.amount);
    const discount = form.discount === "" ? 0 : Number(form.discount);
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    const safeDiscount = Number.isFinite(discount) ? discount : 0;
    const netAmount = Math.max(0, safeAmount - safeDiscount);
    try {
      await createCTScanRecord(token, {
        patient: Number(form.patient),
        ticket: Number(form.ticket),
        test: Number(form.test),
        amount: netAmount,
        total_amount: netAmount,
        discount: safeDiscount,
        discount_amount: safeDiscount,
        is_urgent: !!form.is_urgent,
        date: form.date,
        notes: form.notes || "",
        status: "Pending",
      });
      setForm({
        ticket: "",
        patient: "",
        patient_name: "",
        test: "",
        amount: "",
        discount: "",
        is_urgent: false,
        date: new Date().toISOString().slice(0, 10),
        notes: "",
      });
      setTicketSearch("");
      setTicketOpen(false);
      await load();
      setSnackbar({ open: true, message: "Data added successfully!", severity: "success" });
    } catch (e) {
      setSnackbar({ open: true, message: "Failed to submit CT Scan referral.", severity: "error" });
    }
  };

  return (
    <>
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t?.("doctor.CT ScanReferralForm", "CT Scan Referral Form")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Ticket Number"
            placeholder="Search by ticket number..."
            value={ticketSearch}
            onFocus={() => setTicketOpen(true)}
            onChange={(e) => {
              const val = e.target.value;
              setTicketSearch(val);
              setForm({ ...form, ticket: "", patient: "", test: "" });
              setTicketOpen(true);
            }}
            helperText={
              ticketSearch.length < 3
                ? "Type at least 3 characters to search"
                : !form.ticket
                  ? "Please select a ticket from results"
                  : " "
            }
            error={ticketSearch.length >= 3 && !form.ticket}
            size="small"
            sx={formInputSx}
          />
          {ticketOpen && ticketSearch.length >= 3 && (
            <Paper sx={{ mt: 1, maxHeight: 220, overflowY: "auto", border: "1px solid #ddd" }}>
              {tickets
                .filter((tk) => (tk.ticket_number || "").toLowerCase().includes(ticketSearch.toLowerCase()))
                .map((tk) => (
                  <MenuItem
                    key={tk.id}
                    onClick={() => {
                      setTicketSearch(tk.ticket_number || "");
                      setForm({
                        ...form,
                        ticket: tk.id,
                        patient: tk.patient || "",
                        patient_name: tk.patient_name || tk.patient_name_display || tk.patient_display?.name || "",
                        test: "",
                      });
                      setTicketOpen(false);
                    }}
                  >
                    <Stack>
                      <Typography fontWeight={600}>{tk.ticket_number}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(tk.patient_name || tk.patient_name_display || "Unknown Patient")} ({`Patient ID: ${tk.patient || "-"}`})
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              {tickets.filter((tk) => (tk.ticket_number || "").toLowerCase().includes(ticketSearch.toLowerCase())).length === 0 && (
                <Typography sx={{ p: 1.5, color: "text.secondary" }}>No tickets found</Typography>
              )}
            </Paper>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Patient"
            value={form.patient_name || tickets.find((tk) => String(tk.id) === String(form.ticket))?.patient_name || tickets.find((tk) => String(tk.id) === String(form.ticket))?.patient_name_display || ""}
            InputProps={{ readOnly: true }}
            size="small"
            sx={formInputSx}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField select fullWidth size="small" label="Select Test" value={form.test} onChange={(e) => handleTestChange(e.target.value)} sx={formInputSx}>
            {tests.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} sx={formInputSx} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth size="small" label="Amount" value={form.amount} InputProps={{ readOnly: true }} sx={formInputSx} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth size="small" type="number" label="Discount" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} sx={formInputSx} />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={!!form.is_urgent}
                onChange={(e) => setForm({ ...form, is_urgent: e.target.checked })}
              />
            }
            label="Urgent"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" multiline minRows={3} label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} sx={formInputSx} />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" fullWidth onClick={handleSubmit} disabled={!form.ticket || !form.patient || !form.test} sx={{ bgcolor: "#1f6f8b", "&:hover": { bgcolor: "#185b72" }, textTransform: "none", fontWeight: 700 }}>
            Submit
          </Button>
        </Grid>
      </Grid>
    </Paper>
    <Snackbar
      open={snackbar.open}
      autoHideDuration={5000}
      onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Paper
        elevation={4}
        sx={{
          width: 338,
          borderRadius: "6px",
          bgcolor: "#f3f3f3",
          overflow: "hidden",
          "@keyframes ctScanToastProgressLtr": {
            from: { transform: "scaleX(1)" },
            to: { transform: "scaleX(0)" },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", px: 1.45, py: 0.82 }}>
          <CheckCircleIcon
            sx={{
              color: snackbar.severity === "success" ? "#17b629" : "#d32f2f",
              mr: 1,
              fontSize: 20,
            }}
          />
          <Typography
            sx={{ color: "#666", fontSize: "0.9rem", flexGrow: 1, lineHeight: 1.2 }}
          >
            {snackbar.message}
          </Typography>
          <CloseIcon
            onClick={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            sx={{ color: "#9a9a9a", cursor: "pointer", fontSize: 20 }}
          />
        </Box>
        <Box
          sx={{
            height: 5,
            bgcolor: snackbar.severity === "success" ? "#22c841" : "#d32f2f",
            width: "100%",
            transform: "scaleX(1)",
            transformOrigin: "left",
            animation: "ctScanToastProgressLtr 5s linear forwards",
          }}
        />
      </Paper>
    </Snackbar>
    </>
  );
};

export default CTScanReferral;
















